/**
 * Planificación sobre Supabase (ex Firestore). Los writeBatch originales
 * (match/unmatch, reordenamiento) pasan a awaits secuenciales: el orden de las
 * operaciones está pensado para que un fallo parcial deje un estado recuperable.
 */
import {
  subscribeUser,
  listUserRows,
  insertRow,
  updateRow,
  deleteRow,
  handleDbError,
} from '../lib/db';
import { PlannedConcept, PlannedItem, PlannedMatchGroup, PlanningCategory } from '../types';

enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

// Conceptos
export const seedDefaultCategories = async (userId: string) => {
  const defaults = [
    { id: 'ORDINARY', name: 'Ordinario' },
    { id: 'EXTRAORDINARY', name: 'Extraordinario' }
  ];

  try {
    // nota: en Postgres el id es uuid autogenerado; la idempotencia del setDoc
    // con id fijo se preserva chequeando por nombre antes de insertar.
    const existing = await listUserRows<PlanningCategory>('planningCategories', userId);
    for (const def of defaults) {
      if (existing.some((cat) => cat.name === def.name)) continue;
      await insertRow('planningCategories', {
        name: def.name,
        userId,
        createdAt: new Date().toISOString()
      });
    }
  } catch (error) {
    handleDbError(error, OperationType.WRITE, `users/${userId}/planningCategories`);
  }
};

export const getPlanningCategories = (userId: string, callback: (categories: PlanningCategory[]) => void) => {
  return subscribeUser<PlanningCategory>(
    'planningCategories',
    userId,
    callback,
    { orderBy: 'name', ascending: true },
    (error) => {
      try {
        handleDbError(error, OperationType.GET, `users/${userId}/planningCategories`);
      } catch {
        /* logueado; la suscripción sigue viva */
      }
    },
  );
};

export const createPlanningCategory = async (userId: string, data: Omit<PlanningCategory, 'id' | 'createdAt'>) => {
  try {
    return await insertRow<PlanningCategory>('planningCategories', {
      ...data,
      userId,
      createdAt: new Date().toISOString()
    });
  } catch (error) {
    handleDbError(error, OperationType.CREATE, `users/${userId}/planningCategories`);
  }
};

export const updatePlanningCategory = async (userId: string, categoryId: string, data: Partial<PlanningCategory>) => {
  try {
    return await updateRow('planningCategories', categoryId, data);
  } catch (error) {
    handleDbError(error, OperationType.UPDATE, `users/${userId}/planningCategories/${categoryId}`);
  }
};

export const deletePlanningCategory = async (userId: string, categoryId: string) => {
  try {
    return await deleteRow('planningCategories', categoryId);
  } catch (error) {
    handleDbError(error, OperationType.DELETE, `users/${userId}/planningCategories/${categoryId}`);
  }
};

// Conceptos
export const getPlannedConcepts = (userId: string, callback: (concepts: PlannedConcept[]) => void) => {
  return subscribeUser<PlannedConcept>(
    'plannedConcepts',
    userId,
    callback,
    { orderBy: 'createdAt', ascending: false },
    (error) => {
      try {
        handleDbError(error, OperationType.GET, `users/${userId}/plannedConcepts`);
      } catch {
        /* logueado; la suscripción sigue viva */
      }
    },
  );
};

export const createPlannedConcept = async (userId: string, data: Omit<PlannedConcept, 'id' | 'createdAt'>) => {
  try {
    return await insertRow<PlannedConcept>('plannedConcepts', {
      ...data,
      userId,
      createdAt: new Date().toISOString()
    });
  } catch (error) {
    handleDbError(error, OperationType.CREATE, `users/${userId}/plannedConcepts`);
  }
};

export const updatePlannedConcept = async (userId: string, conceptId: string, data: Partial<PlannedConcept>) => {
  try {
    return await updateRow('plannedConcepts', conceptId, data);
  } catch (error) {
    handleDbError(error, OperationType.UPDATE, `users/${userId}/plannedConcepts/${conceptId}`);
  }
};

export const deletePlannedConcept = async (userId: string, conceptId: string) => {
  // Option: Maybe check if items exist, but for now just delete
  try {
    return await deleteRow('plannedConcepts', conceptId);
  } catch (error) {
    handleDbError(error, OperationType.DELETE, `users/${userId}/plannedConcepts/${conceptId}`);
  }
};

// Ítems para un periodo (YYYY-MM)
export const getPlannedItemsByPeriod = (userId: string, period: string, callback: (items: PlannedItem[]) => void) => {
  // El where('period','==',period) de Firestore se resuelve filtrando en JS
  // sobre la suscripción completa del usuario.
  return subscribeUser<PlannedItem>(
    'plannedItems',
    userId,
    (items) => callback(items.filter((item) => item.period === period)),
    undefined,
    (error) => {
      try {
        handleDbError(error, OperationType.GET, `users/${userId}/plannedItems`);
      } catch {
        /* logueado; la suscripción sigue viva */
      }
    },
  );
};

export const getAllPlannedItems = (userId: string, callback: (items: PlannedItem[]) => void) => {
  return subscribeUser<PlannedItem>(
    'plannedItems',
    userId,
    callback,
    { orderBy: 'date', ascending: true },
    (error) => {
      try {
        handleDbError(error, OperationType.GET, `users/${userId}/plannedItems`);
      } catch {
        /* logueado; la suscripción sigue viva */
      }
    },
  );
};

export const createPlannedItem = async (userId: string, data: Omit<PlannedItem, 'id'>) => {
  try {
    return await insertRow<PlannedItem>('plannedItems', { ...data, userId });
  } catch (error) {
    handleDbError(error, OperationType.CREATE, `users/${userId}/plannedItems`);
  }
};

export const updatePlannedItem = async (userId: string, itemId: string, data: Partial<PlannedItem>) => {
  try {
    return await updateRow('plannedItems', itemId, data);
  } catch (error) {
    handleDbError(error, OperationType.UPDATE, `users/${userId}/plannedItems/${itemId}`);
  }
};

export const deletePlannedItem = async (userId: string, itemId: string) => {
  try {
    return await deleteRow('plannedItems', itemId);
  } catch (error) {
    handleDbError(error, OperationType.DELETE, `users/${userId}/plannedItems/${itemId}`);
  }
};

export const updatePlannedItemsOrder = async (userId: string, items: { id: string; orderIndex: number }[]) => {
  // nota: sin batch atómico; orden pensado para fallo parcial seguro
  // (cada update es independiente; un fallo deja el resto del orden intacto).
  try {
    for (const item of items) {
      await updateRow('plannedItems', item.id, { orderIndex: item.orderIndex });
    }
  } catch (error) {
    handleDbError(error, OperationType.UPDATE, `users/${userId}/plannedItems`);
  }
};

// Match
export const getPlannedMatchGroups = (userId: string, callback: (groups: PlannedMatchGroup[]) => void) => {
  return subscribeUser<PlannedMatchGroup>(
    'plannedMatches',
    userId,
    callback,
    undefined,
    (error) => {
      try {
        handleDbError(error, OperationType.GET, `users/${userId}/plannedMatches`);
      } catch {
        /* logueado; la suscripción sigue viva */
      }
    },
  );
};

export const matchTransactionsToItems = async (
  userId: string,
  transactionIds: string[],
  plannedItemIds: string[],
  accumulativeItemIds: string[] = [], // Optional for backward compatibility
  isPartial: boolean = false
) => {
  // nota: sin batch atómico; orden pensado para fallo parcial seguro
  // (el grupo de match se crea primero: si fallan los updates de status,
  // los ítems quedan PENDING y unmatchGroup permite revertir el grupo).
  try {
    // Crear el grupo de match
    await insertRow('plannedMatches', {
      plannedItemIds,
      transactionIds,
      userId,
      createdAt: new Date().toISOString()
    });

    // Marcar los planned items como COMPLETED SOLO si no son acumulativos Y NO ES PARCIAL
    if (!isPartial) {
      for (const itemId of plannedItemIds) {
        if (!accumulativeItemIds.includes(itemId)) {
          await updateRow('plannedItems', itemId, { status: 'COMPLETED' });
        }
      }
    }
  } catch (error) {
    handleDbError(error, OperationType.WRITE, `users/${userId}/plannedMatches`);
  }
};

export const unmatchGroup = async (userId: string, matchGroup: PlannedMatchGroup) => {
  // nota: sin batch atómico; orden pensado para fallo parcial seguro
  // (primero se revierten los status: si falla el delete, el grupo sigue
  // existiendo y el unmatch puede reintentarse).
  try {
    // Revert status for planned items
    for (const itemId of matchGroup.plannedItemIds) {
      await updateRow('plannedItems', itemId, { status: 'PENDING' });
    }

    // Delete the match group
    await deleteRow('plannedMatches', matchGroup.id);
  } catch (error) {
    handleDbError(error, OperationType.WRITE, `users/${userId}/plannedMatches/${matchGroup.id}`);
  }
};

// Helper: Proyectar periodos
export const projectConceptToPeriod = async (userId: string, concept: PlannedConcept, period: string, baseDate: string, index: number) => {
  return createPlannedItem(userId, {
    conceptId: concept.id,
    title: concept.title,
    type: concept.type,
    category: concept.category,
    amount: concept.defaultAmount,
    period,
    date: baseDate,
    status: 'PENDING',
    orderIndex: index
  });
};
